import { useState } from "react";

import { hhmmToMinutes, minutesToHHMM, weekdayLabel } from "../../lib/format";
import { t } from "../../lib/i18n";

import { Button } from "./Button";
import { TimeField } from "./TimeField";

export interface WindowItem {
  id: string;
  weekday: number;
  start_minute: number;
  end_minute: number;
}

interface WeeklyScheduleEditorProps {
  windows: WindowItem[];
  onAdd: (window: { weekday: number; start_minute: number; end_minute: number }) => void;
  onRemove: (windowId: string) => void;
  busy?: boolean;
  /**
   * IANA timezone label rendered above the time inputs so operators know
   * which frame "09:00" refers to. The actual minute-of-day values stored on
   * the backend are timezone-agnostic; this is purely a clarification.
   */
  profileTimezone?: string;
}

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

export function WeeklyScheduleEditor({
  windows,
  onAdd,
  onRemove,
  busy,
  profileTimezone,
}: WeeklyScheduleEditorProps) {
  const [draftWeekday, setDraftWeekday] = useState<number | null>(null);
  const [draftStart, setDraftStart] = useState("09:00");
  const [draftEnd, setDraftEnd] = useState("18:00");

  const grouped = WEEKDAYS.map((wd) => ({
    weekday: wd,
    windows: windows
      .filter((w) => w.weekday === wd)
      .sort((a, b) => a.start_minute - b.start_minute),
  }));

  function commitDraft() {
    if (draftWeekday === null) return;
    const start = hhmmToMinutes(draftStart);
    const end = hhmmToMinutes(draftEnd);
    if (end <= start) return;
    onAdd({ weekday: draftWeekday, start_minute: start, end_minute: end });
    setDraftWeekday(null);
    setDraftStart("09:00");
    setDraftEnd("18:00");
  }

  return (
    <div className="weekly-editor">
      {profileTimezone && (
        <div
          className="muted"
          style={{ fontSize: 12, marginBottom: 8 }}
        >
          {t("schedules.windows.timezoneHint").replace("{{tz}}", profileTimezone)}
        </div>
      )}
      {grouped.map(({ weekday, windows: dayWindows }) => (
        <DayRow
          key={weekday}
          weekday={weekday}
          windows={dayWindows}
          onAddStart={() => {
            setDraftWeekday(weekday);
            setDraftStart("09:00");
            setDraftEnd("18:00");
          }}
          onRemove={onRemove}
          busy={busy}
          draftActive={draftWeekday === weekday}
          draftStart={draftStart}
          draftEnd={draftEnd}
          onDraftChange={(s, e) => {
            setDraftStart(s);
            setDraftEnd(e);
          }}
          onCommit={commitDraft}
          onCancelDraft={() => setDraftWeekday(null)}
        />
      ))}
    </div>
  );
}

interface DayRowProps {
  weekday: number;
  windows: WindowItem[];
  onAddStart: () => void;
  onRemove: (windowId: string) => void;
  busy?: boolean;
  draftActive: boolean;
  draftStart: string;
  draftEnd: string;
  onDraftChange: (start: string, end: string) => void;
  onCommit: () => void;
  onCancelDraft: () => void;
}

function DayRow({
  weekday,
  windows,
  onAddStart,
  onRemove,
  busy,
  draftActive,
  draftStart,
  draftEnd,
  onDraftChange,
  onCommit,
  onCancelDraft,
}: DayRowProps) {
  return (
    <div className="weekly-editor__row">
      <div className="weekly-editor__day">{weekdayLabel(weekday)}</div>
      <div className="weekly-editor__windows">
        {windows.map((w) => (
          <span key={w.id} className="weekly-editor__chip">
            <span>{minutesToHHMM(w.start_minute)}</span>
            <span>–</span>
            <span>{minutesToHHMM(w.end_minute)}</span>
            <button
              type="button"
              className="weekly-editor__chip-remove"
              onClick={() => onRemove(w.id)}
              disabled={busy}
              aria-label={t("common.remove")}
            >
              ×
            </button>
          </span>
        ))}
        {draftActive ? (
          <span
            className="weekly-editor__chip"
            style={{ background: "var(--color-surface)" }}
          >
            <TimeField
              value={draftStart}
              onChange={(v) => onDraftChange(v, draftEnd)}
              ariaLabel="Start time"
              style={{ height: 24 }}
            />
            <span>–</span>
            <TimeField
              value={draftEnd}
              onChange={(v) => onDraftChange(draftStart, v)}
              ariaLabel="End time"
              style={{ height: 24 }}
            />
            <Button size="sm" onClick={onCommit} disabled={busy}>
              {t("common.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancelDraft}>
              {t("common.cancel")}
            </Button>
          </span>
        ) : (
          <button
            type="button"
            className="weekly-editor__add"
            onClick={onAddStart}
            disabled={busy}
          >
            + {t("schedules.windows.add")}
          </button>
        )}
      </div>
    </div>
  );
}
